import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
} from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { BehaviorSubject, Subject } from "rxjs";
import {
  CredentialVerification,
  ICredentialVerification,
} from "./../../../shared/models/products/credential-verification";
import { Address, IAddress } from "./../../../shared/models/address";

import { NotificationsService } from "./../../../../shared/services/notifications.service";
import { ViewStateService } from "./../../../../shared/services/view-state.service";
import { CommonApiService } from "./../../../../shared/services/api/common-api.service";
import { PurchaseStateService } from "./../../../shared/services/purchase-state.service";
import { UserService } from "./../../../../shared/services/user.service";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-state-licensure",
  templateUrl: "./state-licensure.component.html",
})
export class StateLicensureComponent implements OnInit, OnDestroy {
  stateLicensureForm: UntypedFormGroup;
  agencies = [];
  selectedAgency: any;
  product: ICredentialVerification;
  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private viewState: ViewStateService,
    private notifications: NotificationsService,
    private commonApiService: CommonApiService,
    private purchaseState: PurchaseStateService,
    private router: Router,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.viewState.startLoading();

    this.product = <CredentialVerification>(
      this.purchaseState.getTempProductData()
    );

    this.buildForm();
    this.requestAgencies();

    this.stateLicensureForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.setSelectedAgency(parseInt(data.agencyId));
      });
  }

  isExpired(): boolean {
    if (
      this.userService.CurrentUser.hasExpiredCredentials &&
      !this.userService.CurrentUser.credentialHolder
    ) {
      return true;
    }
    return false;
  }

  onSubmit() {
    let address;
    this.validateForm();
    if (this.stateLicensureForm.valid) {
      address = new Address();
      let agency = this.agencies.find((a) => {
        return a.id == this.stateLicensureForm.value.agencyId;
      });
      address.line1 = agency.agencyName;
      //2022.01.03 They no longer want agency to show the physical address. Uncomment the following to re-enable.
      //address.line2 = agency.addressLine1;
      //address.line3 = agency.addressLine2;
      //address.city = agency.city;
      //address.state = agency.stateCode;
      //address.postalCode = agency.zip;
      //address.postalCodePlus4 = agency.zipPlus4;

      this.product.agencyId = this.stateLicensureForm.value.agencyId;
      this.product.sendTo = address || new Address();
      this.purchaseState.patchProducts(this.product);
      this.router.navigate(["purchase/checkout"]);
    }
  }

  onBack(event: Event) {
    event.preventDefault();

    this.router.navigate([this.purchaseState.previousRouteUrl]);
  }

  private requestAgencies() {
    this.commonApiService
      .getStateLicensures()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.agencies = res;
        this.setSelectedAgency(parseInt(this.stateLicensureForm.value.id));
        this.viewState.stopLoading();
      });
  }

  private setSelectedAgency(agencyId) {
    if (agencyId) {
      this.selectedAgency = this.agencies.find((agency) => {
        return agency.id === agencyId;
      });
    }
  }

  private buildForm(): void {
    this.stateLicensureForm = this.formBuilder.group({
      agencyId: [this.formValues.agencyId, [Validators.required]],
    });
  }

  private validateForm() {
    for (const control in this.stateLicensureForm.controls) {
      this.stateLicensureForm.controls[control].updateValueAndValidity();
    }

    let hasErrors = false;
    for (const field in this.formErrors) {
      this.formErrors[field] = "";
      const control = this.stateLicensureForm.get(field);
      if (control && control.invalid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + " ";
        }
      }
    }
    if (hasErrors) {
      this.notifications.error(
        "Fields Invalid",
        "Please review the form and resubmit."
      );
      this.viewState.stopLoading();
    }
  }

  private formValues = {
    agencyId: "",
  };

  formErrors = {
    agencyId: "",
  };

  validationMessages = {
    agencyId: {
      required: "State Licensure Agency is required.",
    },
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
